/* FILE: ./frontend/src/components/ArbitrageMatrix.jsx */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next'; // ⚡ ADDED

const ArbitrageMatrix = ({ selectedSuppliers, buyerProvince, buyerPostalCode, targetHsCode }) => {
  const { t, i18n } = useTranslation(); // ⚡ UPDATED: Destructured i18n for headers
  const [matrixData, setMatrixData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Global Error state to catch API Limits
  const [globalError, setGlobalError] = useState(null);
  
  const navigate = useNavigate();

  // Define the active code and the dynamic display label
  const hasValidSearch = targetHsCode && targetHsCode !== 'Unknown' && targetHsCode.trim() !== '';
  const activeHsCode = hasValidSearch ? targetHsCode : '7308.90'; // Default to Structural Steel
  
  const displayProduct = hasValidSearch 
    ? `"${targetHsCode}"` 
    : `Structural Steel (HS: ${activeHsCode})`;

  const extractRealPrice = (supplier, searchTerm) => {
    const rootPrice = supplier.catalogPrice || supplier.price_cad || supplier.price || supplier.unit_price || supplier.base_price || supplier.cost;
    if (rootPrice) return rootPrice;

    if (supplier.products && Array.isArray(supplier.products)) {
        if (searchTerm && searchTerm !== 'Unknown') {
            const cleanTerm = searchTerm.toLowerCase();
            const matchedProd = supplier.products.find(p => 
                (p.hs_code && p.hs_code.toLowerCase().includes(cleanTerm)) || 
                (p.name && p.name.toLowerCase().includes(cleanTerm))
            );
            if (matchedProd) {
                return matchedProd.price_cad || matchedProd.price || matchedProd.unit_price || matchedProd.base_price || matchedProd.cost;
            }
        }
        
        if (supplier.products.length > 0) {
            const p = supplier.products[0];
            return p.price_cad || p.price || p.unit_price || p.base_price || p.cost;
        }
    }
    
    return null; 
  };

  useEffect(() => {
    const generateMatrix = async () => {
      if (!selectedSuppliers || selectedSuppliers.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setGlobalError(null); 
      setMatrixData([]);    

      // Prepare API constraints and Auth scope
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      const activeBuyerProvince = buyerProvince || 'ON'; 

      // ⚡ HELPER: Silent Audit Logger
      const logAudit = (status, errorMsg = null) => {
          if (!userId) return;
          fetch(`${apiUrl}/audit/log`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-user-id': userId },
              body: JSON.stringify({
                  action_type: 'ARBITRAGE_MATRIX',
                  status: status,
                  error_message: errorMsg,
                  parameters: { target_hs_code: activeHsCode, destination: activeBuyerProvince, supplier_count: selectedSuppliers.length }
              })
          }).catch(() => {}); // Fire and forget
      };

      try {
        const evaluations = [];

        for (const supplier of selectedSuppliers) {
          const realPrice = extractRealPrice(supplier, targetHsCode);
          const simulationValue = realPrice || 1000;

          try {
            const response = await axios.post(`${apiUrl}/compliance/simulate`, {
              origin: supplier.province,
              destination: activeBuyerProvince,
              hs_code: activeHsCode,
              value: simulationValue 
            }, {
              headers: {
                'x-user-id': userId, 
                'Accept-Language': i18n.language // ⚡ BILINGUAL HEADER INJECTED
              }
            });

            evaluations.push({
              ...supplier,
              displayPrice: realPrice, 
              compliance: response.data,
              logisticsFriction: supplier.province === activeBuyerProvince ? 'Low (Intra-provincial)' : 'High (Inter-provincial LTL)'
            });
            setMatrixData([...evaluations]); 

          } catch (err) {
            console.error(`Failed to analyze supplier ${supplier.id}`, err);
            if (err.response?.data?.code === 'LIMIT_REACHED' || err.response?.data?.code === 'PRO_LIMIT_REACHED') {
                setGlobalError({
                    type: 'limit',
                    title: t('matrix.limit_title'),
                    message: err.response.data.code === 'PRO_LIMIT_REACHED' 
                      ? t('matrix.pro_limit_msg')
                      : t('matrix.free_limit_msg')
                });
                setLoading(false);
                
                // ⚡ LOG LIMIT ERROR
                logAudit('ERROR', `Limit Reached: ${err.response.data.code}`);
                return; 
            }

            evaluations.push({
              ...supplier,
              displayPrice: realPrice,
              compliance: null,
              logisticsFriction: 'Unknown'
            });
            setMatrixData([...evaluations]); 
          }

          // Throttle slightly so we don't bombard the API instantly
          await new Promise(resolve => setTimeout(resolve, 1000));
        }

        setLoading(false);

        // ⚡ LOG MATRIX SUCCESS
        logAudit('SUCCESS');

      } catch (err) {
        setError(t('matrix.fail_gen'));
        setLoading(false);

        // ⚡ LOG CRITICAL FAILURE
        logAudit('ERROR', err.message);
      }
    };

    generateMatrix();
  }, [selectedSuppliers, buyerProvince, targetHsCode, activeHsCode, t, i18n.language]);

  const handleAddToRfq = (supplier) => {
    navigate('/rfq/new', { 
      state: { 
        preselectedSupplier: supplier,
        autoFillCode: activeHsCode, 
        autoFillProvince: buyerProvince || 'ON'
      } 
    });
  };

  const handleDeepAudit = (supplier) => {
    navigate('/simulator', {
      state: {
        autoFillCode: activeHsCode,
        autoFillOrigin: supplier.province,
        autoFillDestination: buyerProvince || 'ON',
        autoFillValue: supplier.displayPrice || 1000,
        autoFillOriginPostal: supplier.postal_code || '',
        autoFillDestPostal: buyerPostalCode || '',
        existingFlashSummary: supplier.compliance?.flashSummary || null,
        existingCompliance: supplier.compliance || null, // ⚡ Prevents double simulation run
        autoRunAudit: true 
      }
    });
  };

  if (loading && matrixData.length === 0 && !globalError) {
    return (
      <div className="flex justify-center items-center p-12 bg-white rounded-xl shadow-sm border border-gray-100 mt-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900"></div>
        <span className="ml-3 text-gray-600 font-medium">{t('matrix.booting')}</span>
      </div>
    );
  }

  if (globalError) {
      return (
          <div className="bg-red-50 border border-red-200 p-8 rounded-xl text-center mt-8 shadow-sm">
              <div className="text-4xl mb-4">🛡️</div>
              <h3 className="text-xl font-bold text-red-900 mb-2">{globalError.title}</h3>
              <p className="text-red-700 text-sm mb-6 max-w-md mx-auto">{globalError.message}</p>
              <button 
               onClick={() => navigate('/billing')} 
                  className="bg-red-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-red-700 shadow-md transition-colors"
              >
                  {t('matrix.upgrade_btn')}
              </button>
          </div>
      );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg border border-red-200 mt-8">
        {error}
      </div>
    );
  }

  if (matrixData.length === 0 && !loading) {
    return (
      <div className="p-8 text-center bg-gray-50 rounded-xl border border-gray-200 border-dashed mt-8">
        <p className="text-gray-500">{t('matrix.empty_state')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-200 mt-8">
      <div className="px-6 py-5 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-bold text-gray-900">{t('matrix.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            <Trans i18nKey="matrix.subtitle" values={{ product: displayProduct, province: buyerProvince || 'ON' }}>
              Compare landed variables for <span className="font-bold text-blue-700">{displayProduct}</span> shipped to <span className="font-semibold text-gray-800">{buyerProvince || 'ON'}</span>.
            </Trans>
          </p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>}
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {matrixData.length} / {selectedSuppliers.length} {t('matrix.processed')}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_supplier')}
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_location')}
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_base')}
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_tax')}
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_logistics')}
              </th>
              <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_cfta')}
              </th>
              <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">
                {t('matrix.col_action')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {matrixData.map((supplier, idx) => (
              <tr key={supplier.id || idx} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="ml-4">
                      <div className="text-sm font-bold text-gray-900">{supplier.company_name}</div>
                      <div className="text-sm text-gray-500">Tier: {supplier.compliance_tier?.includes('Tier 1') ? t('matrix.tier_verified') : t('matrix.tier_standard')}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-semibold rounded-md bg-gray-100 text-gray-800">
                    {supplier.province}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                  {supplier.displayPrice ? `$${Number(supplier.displayPrice).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : t('matrix.req_rfq')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {supplier.compliance ? supplier.compliance.financials?.tax_details : t('matrix.error')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {supplier.logisticsFriction}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {supplier.compliance && supplier.compliance.traffic_light === 'green' && (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {t('matrix.cleared')}
                    </span>
                  )}
                  {supplier.compliance && supplier.compliance.traffic_light === 'yellow' && (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      {t('matrix.caution')}
                    </span>
                  )}
                  {supplier.compliance && supplier.compliance.traffic_light === 'red' && (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      {t('matrix.blocked')}
                    </span>
                  )}
                  {!supplier.compliance && (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      {t('matrix.error')}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button 
                      onClick={() => handleDeepAudit(supplier)}
                      className="text-indigo-600 hover:text-indigo-900 font-bold border border-indigo-200 px-3 py-1 rounded hover:bg-indigo-50 transition-colors flex items-center gap-1"
                      title="Run AI Compliance Audit"
                    >
                      <span>✨</span> {t('matrix.audit')}
                    </button>
                    <button 
                      onClick={() => handleAddToRfq(supplier)}
                      className="text-blue-600 hover:text-blue-900 font-bold border border-blue-200 px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                    >
                      {t('matrix.add_rfq')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* ⚡ NEW AI MATRIX REGULATORY DISCLAIMER */}
      <div className="bg-amber-50/50 px-6 py-4 border-t border-amber-100 text-xs text-amber-800 leading-relaxed">
        <strong className="text-amber-900">{t('matrix.reg_notice')}</strong> {t('matrix.reg_desc')}
      </div>
    </div>
  );
};

export default ArbitrageMatrix;